/**
 * Dashboard utility functions for trip statistics and day calculations.
 */

import { extractLocationsWithCoordinates, normalizeContent } from '../../utils/itineraryParser';

// Words that should NOT be treated as location names (exact match only)
const INVALID_LOCATION_NAMES = [
  'coordinates', 'coordinate', 'gps', 'location', 'address',
  'latitude', 'longitude', 'lat', 'lng', 'coord', 'coords'
];

/**
 * Filter out invalid location names from extracted locations.
 * Keep this simple to avoid filtering out real locations.
 */
const filterValidLocations = (locations) => {
  return locations.filter(loc => {
    const nameLower = loc.name.toLowerCase().trim();

    // Filter out if name is too short
    if (nameLower.length < 2) return false;

    // Filter out if name is just numbers/coordinates
    if (/^[\d\s.,\-()]+$/.test(loc.name.trim())) return false;

    // Filter out exact matches of invalid names
    if (INVALID_LOCATION_NAMES.includes(nameLower)) return false;

    // Filter out names that are just "Coordinates" or similar
    if (/^coordinates?$/i.test(nameLower)) return false;
    if (/^gps\s*coordinates?$/i.test(nameLower)) return false;

    return true;
  });
};

/**
 * Check if a day title is a summary header (not a real day with stops).
 * Examples: "Toronto, Ontario (5 full days)", "** Paris (3 days)"
 */
const isSummaryHeader = (title) => {
  if (!title) return false;
  // Check for patterns like "(X days)", "(X full days)", "(X-day)"
  if (/\(\d+\s*(full\s+)?days?\)/i.test(title)) return true;
  // Check for patterns like "X-day" at the end
  if (/\d+-day\s*(trip|tour|itinerary)?$/i.test(title)) return true;
  return false;
};

/**
 * Extract days from itinerary content, splitting by day headers and extracting locations.
 * Uses the same day detection logic as the main itinerary display.
 * Only returns days that have actual stops planned.
 *
 * @param {string} content - The itinerary content
 * @returns {Array} Array of day objects with day number, title, and locations
 */
export const extractDaysFromContent = (content) => {
  if (!content) return [];

  const normalized = normalizeContent(content);
  const lines = normalized.split('\n');
  const days = [];
  let currentDay = null;
  let currentDayContent = [];

  // Stricter pattern to match day headers: must start with Day/Part followed by number
  // Matches: "Day 1", "**Day 1:", "## Day 1 -", "Day 1: Toronto"
  // Does NOT match: "Toronto (5 days)", "5 full days"
  const dayPattern = /^(?:#{1,3}\s*)?\*?\*?\s*(?:Day|Part)\s+(\d+)\s*[:\-\s]*(.*?)(?:\*\*)?$/i;

  const processCurrentDay = () => {
    if (currentDay !== null) {
      const dayContent = currentDayContent.join('\n');
      const rawLocations = extractLocationsWithCoordinates(dayContent);
      const locations = filterValidLocations(rawLocations);

      // Only add if:
      // 1. We haven't already added this day number
      // 2. The title is not a summary header (like "Toronto (5 days)")
      if (!days.some(d => d.day === currentDay.day) &&
          !isSummaryHeader(currentDay.title)) {
        days.push({
          day: currentDay.day,
          dayTitle: currentDay.title,
          locations: locations
        });
      }
    }
  };

  lines.forEach((line) => {
    const trimmedLine = line.trim();
    const dayMatch = trimmedLine.match(dayPattern);

    if (dayMatch) {
      // Save previous day before starting new one
      processCurrentDay();

      const dayNum = parseInt(dayMatch[1], 10);
      let title = dayMatch[2]?.trim().replace(/\*\*$/, '').trim() || `Day ${dayNum}`;
      // Clean up title - remove leading ** or markdown
      title = title.replace(/^\*\*\s*/, '').replace(/\s*\*\*$/, '');

      currentDay = { day: dayNum, title: title };
      currentDayContent = [line];
    } else if (currentDay !== null) {
      // A top-level (H1/H2) heading that is not a day marks the end of the
      // itinerary days (e.g. "## Checklist", "## Other Points of Interest").
      // Close the current day so trailing sections aren't folded into the last
      // day's locations/route. Note: day headers (matched above) and H3+ sub-
      // headers inside a day are unaffected.
      if (/^#{1,2}\s+\S/.test(trimmedLine)) {
        processCurrentDay();
        currentDay = null;
      } else {
        currentDayContent.push(line);
      }
    }
  });

  // Don't forget the last day
  processCurrentDay();

  // Sort by day number
  return days.sort((a, b) => a.day - b.day);
};

/**
 * Get the current day number of the trip based on start date.
 * Returns null if trip hasn't started yet.
 *
 * @param {string} startDate - Trip start date (YYYY-MM-DD)
 * @returns {number|null} Current day number (1-indexed) or null
 */
export const getCurrentDayNumber = (startDate) => {
  if (!startDate) return null;

  const start = new Date(startDate + 'T00:00:00');
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const diffTime = today - start;
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

  // Return day number if trip has started (diffDays >= 0)
  return diffDays >= 0 ? diffDays + 1 : null;
};

/**
 * Get the total number of days in the trip.
 *
 * @param {string} startDate - Trip start date (YYYY-MM-DD)
 * @param {string} endDate - Trip end date (YYYY-MM-DD)
 * @returns {number} Total number of days
 */
export const getTotalTripDays = (startDate, endDate) => {
  if (!startDate || !endDate) return 0;

  const start = new Date(startDate + 'T00:00:00');
  const end = new Date(endDate + 'T00:00:00');

  const diffTime = end - start;
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

  return Math.max(1, diffDays + 1);
};

/**
 * Calculate trip statistics from days and attraction statuses.
 *
 * @param {Array} days - Array of day objects with locations
 * @param {Object} attractionStatuses - Map of attraction statuses keyed by attraction_key
 * @returns {Object} Trip statistics
 */
export const getTripStats = (days, attractionStatuses) => {
  let totalStops = 0;
  let visitedStops = 0;

  days.forEach((day) => {
    if (day.locations) {
      day.locations.forEach((loc) => {
        totalStops++;
        const key = getAttractionKey(loc.name, loc.lat, loc.lng, day.day);
        if (attractionStatuses[key]?.visited) {
          visitedStops++;
        }
      });
    }
  });

  return {
    totalDays: days.length,
    totalStops,
    visitedStops,
    completionPercentage: totalStops > 0 ? Math.round((visitedStops / totalStops) * 100) : 0,
  };
};

/**
 * Get day statistics for a specific day.
 *
 * @param {Object} day - Day object with locations
 * @param {Object} attractionStatuses - Map of attraction statuses
 * @returns {Object} Day statistics
 */
export const getDayStats = (day, attractionStatuses) => {
  const stops = day.locations || [];
  let visitedCount = 0;
  let inProgressCount = 0;

  stops.forEach((loc) => {
    const key = getAttractionKey(loc.name, loc.lat, loc.lng, day.day);
    const status = attractionStatuses[key];
    if (status?.visited) {
      visitedCount++;
    } else if (status?.custom_time) {
      // Has time set but not visited = in progress
      inProgressCount++;
    }
  });

  return {
    totalStops: stops.length,
    visitedCount,
    inProgressCount,
    upcomingCount: stops.length - visitedCount - inProgressCount,
  };
};

/**
 * Generate a unique key for an attraction.
 *
 * @param {string} name - Attraction name
 * @param {number} lat - Latitude
 * @param {number} lng - Longitude
 * @param {number} dayNumber - Day number
 * @returns {string} Unique attraction key
 */
export const getAttractionKey = (name, lat, lng, dayNumber) => {
  if (lat && lng) {
    return `${name.substring(0, 50)}_${lat.toFixed(4)}_${lng.toFixed(4)}`;
  }
  return `${name.substring(0, 50)}_${dayNumber}`;
};

/**
 * Get status for an attraction (visited, in-progress, upcoming).
 *
 * @param {Object} loc - Location object
 * @param {number} dayNumber - Day number
 * @param {Object} attractionStatuses - Map of attraction statuses
 * @returns {string} Status: 'visited', 'in-progress', or 'upcoming'
 */
export const getAttractionStatus = (loc, dayNumber, attractionStatuses) => {
  const key = getAttractionKey(loc.name, loc.lat, loc.lng, dayNumber);
  const status = attractionStatuses[key];

  if (status?.visited) {
    return 'visited';
  }
  if (status?.custom_time) {
    return 'in-progress';
  }
  return 'upcoming';
};

/**
 * Format date for display (e.g., "Fri, Mar 15").
 *
 * @param {string} dateStr - Date string (YYYY-MM-DD)
 * @returns {string} Formatted date
 */
export const formatDayDate = (dateStr) => {
  if (!dateStr) return '';

  try {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return dateStr;
  }
};

/**
 * Calculate the date for a specific day number.
 *
 * @param {string} startDate - Trip start date (YYYY-MM-DD)
 * @param {number} dayNumber - Day number (1-indexed)
 * @returns {string} Date string (YYYY-MM-DD)
 */
export const getDayDate = (startDate, dayNumber) => {
  if (!startDate || !dayNumber) return '';

  const start = new Date(startDate + 'T00:00:00');
  start.setDate(start.getDate() + dayNumber - 1);
  return start.toISOString().split('T')[0];
};

/**
 * Get weather data for a specific day.
 *
 * @param {number} dayNumber - Day number
 * @param {Object} weatherData - Weather data object with forecasts
 * @returns {Object|null} Weather data for the day or null
 */
export const getDayWeather = (dayNumber, weatherData) => {
  if (!weatherData?.forecasts || !dayNumber) return null;
  return weatherData.forecasts[dayNumber] || null;
};

/**
 * Calculate estimated total distance for the trip (in miles).
 *
 * @param {Array} days - Array of day objects with locations
 * @returns {number} Total estimated distance in miles
 */
export const calculateTotalDistance = (days) => {
  let totalKm = 0;

  days.forEach((day) => {
    if (day.locations && day.locations.length >= 2) {
      for (let i = 0; i < day.locations.length - 1; i++) {
        const loc1 = day.locations[i];
        const loc2 = day.locations[i + 1];
        if (loc1.lat && loc1.lng && loc2.lat && loc2.lng) {
          totalKm += calculateDistance(loc1, loc2);
        }
      }
    }
  });

  // Convert km to miles and apply road factor (1.3x straight line)
  const roadKm = totalKm * 1.3;
  const miles = roadKm * 0.621371;

  return Math.round(miles);
};

/**
 * Calculate straight-line distance between two coordinates using Haversine formula.
 *
 * @param {Object} coord1 - {lat, lng}
 * @param {Object} coord2 - {lat, lng}
 * @returns {number} Distance in kilometers
 */
const calculateDistance = (coord1, coord2) => {
  const R = 6371; // Earth's radius in km
  const dLat = toRad(coord2.lat - coord1.lat);
  const dLng = toRad(coord2.lng - coord1.lng);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(coord1.lat)) *
      Math.cos(toRad(coord2.lat)) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

const toRad = (deg) => deg * (Math.PI / 180);
